import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { CheckCircle2, X, XCircle } from 'lucide-react';
import { Button, Field, Textarea, cx } from './primitives';

/* ───────── Toasts ───────── */

type Toast = { id: number; tone: 'success' | 'error'; message: string };
const ToastCtx = createContext<(tone: Toast['tone'], message: string) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((tone: Toast['tone'], message: string) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, tone, message }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4500);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div aria-live="polite" className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-2 px-4 sm:items-end sm:pr-6">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={cx(
              'pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl border bg-white px-4 py-3 text-sm shadow-lg',
              t.tone === 'success' ? 'border-sprout-500/30' : 'border-danger-600/30',
            )}
          >
            {t.tone === 'success' ? <CheckCircle2 className="mt-0.5 size-4 text-sprout-600" /> : <XCircle className="mt-0.5 size-4 text-danger-600" />}
            <span className="flex-1 text-ink">{t.message}</span>
            <button aria-label="Fechar" onClick={() => setToasts((x) => x.filter((y) => y.id !== t.id))} className="text-ink-3 hover:text-ink">
              <X className="size-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const push = useContext(ToastCtx);
  return { success: (m: string) => push('success', m), error: (m: string) => push('error', m) };
}

/* ───────── Diálogo ───────── */

export function Dialog({ open, onClose, title, children, footer }: { open: boolean; onClose: () => void; title: string; children: ReactNode; footer?: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onClick={(e) => e.target === ref.current && onClose()}
      className="m-auto w-[min(32rem,calc(100vw-2rem))] rounded-2xl border border-line bg-white p-0 text-ink shadow-2xl backdrop:bg-ink/40 backdrop:backdrop-blur-[2px]"
    >
      <div className="flex items-center justify-between border-b border-line px-5 py-4">
        <h2 className="font-semibold">{title}</h2>
        <button aria-label="Fechar" onClick={onClose} className="rounded-lg p-1 text-ink-3 hover:bg-paper-2 hover:text-ink">
          <X className="size-4" />
        </button>
      </div>
      <div className="px-5 py-4">{children}</div>
      {footer && <div className="flex flex-wrap justify-end gap-2 border-t border-line bg-paper/60 px-5 py-3">{footer}</div>}
    </dialog>
  );
}

/** Confirmação de ação sensível com justificativa obrigatória (registrada na auditoria). */
export function ReasonDialog({
  open, onClose, title, description, confirmLabel, tone = 'primary', onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  tone?: 'primary' | 'danger';
  onConfirm: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  useEffect(() => {
    if (open) {
      setReason('');
      setError(undefined);
    }
  }, [open]);
  const submit = async () => {
    if (reason.trim().length < 5) return setError('Descreva o motivo (mínimo 5 caracteres)');
    setBusy(true);
    try {
      await onConfirm(reason.trim());
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Falha ao salvar');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button variant={tone} loading={busy} onClick={submit}>{confirmLabel}</Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="text-sm text-ink-2">{description}</div>
        <Field label="Motivo (fica registrado na auditoria)" error={error}>
          {(id, d) => <Textarea id={id} aria-describedby={d} value={reason} onChange={(e) => setReason(e.target.value)} invalid={!!error} autoFocus />}
        </Field>
      </div>
    </Dialog>
  );
}
